import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import {
  addSfFunnel,
  removeFunnel,
  setInitialFunnelMap,
} from './united-canvas/unitedCanvasReducer';
import uniq from 'lodash/uniq';
import { TemplateInterface } from '../components/pages/funnel-map-flow/templates/templatesTypes';
import {
  TransientFunnel,
  PlacingTemplate,
} from '../components/funnel-map-canvas/funnelMapCanvasTypes';
import { FunnelType } from '../types/enums/FunnelType';

export interface MultiFunnelFlowPageNoHistoryState {
  usedInFunnelMapsDialog?: {
    mapsList?: PartialFunnelMap[];
    isLoading: boolean;
    isOpen: boolean;
  };
  funnelStageMetadata: StageMetadata[];
  transientFunnel?: TransientFunnel;
  placingTemplate?: PlacingTemplate;
  hasUnsavedChanges?: boolean;
  isCreateOrAddDialogOpen?: boolean;
  isPageLoading?: boolean;
  hasFetchingErrors?: boolean;
  deployment?: FunnelDeployment;
  isFreshlyCreatedFunnelMap?: boolean;
  isLoadingCursor?: boolean;
}

const initialState: MultiFunnelFlowPageNoHistoryState = {
  funnelStageMetadata: [],
  isPageLoading: true,
};

const multiFunnelFlowPageSlice = createSlice({
  name: 'multiFunnelFlowPageNoHistory',
  initialState,
  reducers: {
    setIsUsedInFunnelMapsDialogOpen: (state) => {
      state.usedInFunnelMapsDialog = {
        isOpen: true,
        isLoading: true,
      };
    },
    setIsPageLoading: (state, action: PayloadAction<{ isPageLoading: boolean }>) => {
      state.isPageLoading = action.payload.isPageLoading;
    },
    setHasFetchingErrors: (state, action: PayloadAction<{ hasErrors: boolean }>) => {
      state.hasFetchingErrors = action.payload.hasErrors;
    },
    closeIsUsedInFunnelMapsDialog: (state) => {
      state.usedInFunnelMapsDialog = undefined;
    },
    setMapsForIsUsedInFunnelMaps: (
      state,
      action: PayloadAction<{
        funnelMaps: PartialFunnelMap[];
      }>,
    ) => {
      state.usedInFunnelMapsDialog = {
        isOpen: true,
        isLoading: false,
        mapsList: action.payload.funnelMaps,
      };
    },
    setFunnelStageMetadata: (
      state,
      action: PayloadAction<{
        stageMetadata?: StageMetadata[];
      }>,
    ) => {
      state.funnelStageMetadata = action.payload.stageMetadata ?? [];
    },
    updateStageMetadataForLeadingField: (
      state,
      action: PayloadAction<{
        leadingFieldStageMetadata?: StageMetadata[];
      }>,
    ) => {
      const { leadingFieldStageMetadata } = action.payload;

      if (leadingFieldStageMetadata && leadingFieldStageMetadata.length) {
        const currentLeadingFieldIds = uniq(
          leadingFieldStageMetadata.map((stageMetadata) => stageMetadata.leadingFieldId),
        );
        const currentStageMetadata = state.funnelStageMetadata;
        const oldStageMetadataWithoutCurrentLeadingField =
          currentStageMetadata &&
          currentStageMetadata.filter(
            (stageMetadata) => !currentLeadingFieldIds.includes(stageMetadata.leadingFieldId),
          );

        state.funnelStageMetadata = [
          ...(Array.isArray(oldStageMetadataWithoutCurrentLeadingField)
            ? oldStageMetadataWithoutCurrentLeadingField
            : []),
          ...leadingFieldStageMetadata,
        ];
      }
    },
    addPlacingTemplate: (
      state,
      action: PayloadAction<{ template: TemplateInterface; useFieldMapping: boolean }>,
    ) => {
      const { template, useFieldMapping } = action.payload;
      state.placingTemplate = { template: template, useFieldMapping };
    },
    removeAllTransientFunnelData: (state) => {
      delete state.transientFunnel;
      delete state.placingTemplate;
    },
    setTransientFunnel: (state, action: PayloadAction<TransientFunnel>) => {
      state.transientFunnel = action.payload;
    },
    setSfNewFunnelTransientDataStage: (
      state,
      action: PayloadAction<{
        stage: SweepStage;
      }>,
    ) => {
      const { stage } = action.payload;
      if (
        state.transientFunnel?.type === FunnelType.SALESFORCE &&
        state.transientFunnel?.importType === 'create'
      ) {
        state.transientFunnel.data.stage = stage;
      }
    },
    dropTransientNewFunnelInPlace: (
      state,
      action: PayloadAction<{ position: FunnelMapPosition }>,
    ) => {
      if (state.transientFunnel) {
        state.transientFunnel.position = action.payload.position;
        state.transientFunnel.isPlacingFunnel = false;
      }
    },
    removeNewFunnelTransientData: (state) => {
      delete state.transientFunnel;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<{ hasUnsavedChanges: boolean }>) => {
      state.hasUnsavedChanges = action.payload.hasUnsavedChanges;
    },
    setIsCreateOrAddDialogOpen: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.isCreateOrAddDialogOpen = action.payload.isOpen;
    },
    setDeployment: (state, action: PayloadAction<{ deployment: FunnelDeployment }>) => {
      state.deployment = action.payload.deployment;
    },
    setFunnelMapIsFreshlyCreated: (state, action: PayloadAction<boolean>) => {
      state.isFreshlyCreatedFunnelMap = action.payload;
    },
    setIsLoadingCursor: (state, action: PayloadAction<boolean>) => {
      state.isLoadingCursor = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addSfFunnel.type, (state) => {
      delete state.transientFunnel;
    });
    builder.addCase(removeFunnel.type, (state) => {
      delete state.transientFunnel;
    });
    builder.addCase(setInitialFunnelMap.type, (state) => {
      delete state.hasUnsavedChanges;
    });
  },
});

export const selectIsUsedInFunnelMapsDialogOpen = (state: RootState) =>
  Boolean(state.multiFunnelFlowPage.usedInFunnelMapsDialog?.isOpen);

export const selectMapsFromIsUsedInFunnelMaps = (state: RootState) =>
  state.multiFunnelFlowPage.usedInFunnelMapsDialog?.mapsList;

export const selectIsUsedInFunnelMapsLoading = (state: RootState) =>
  state.multiFunnelFlowPage.usedInFunnelMapsDialog?.isLoading;

export const selectFunnelStagesMetadata = (state: RootState) =>
  state.multiFunnelFlowPage.funnelStageMetadata;

export const selectPlacingTemplate = (state: RootState) =>
  state.multiFunnelFlowPage.placingTemplate;

export const selectTransientFunnel = (state: RootState) =>
  state.multiFunnelFlowPage.transientFunnel;

export const selectTransientImportSfFunnel = (state: RootState) => {
  if (
    state.multiFunnelFlowPage.transientFunnel?.type === FunnelType.SALESFORCE &&
    state.multiFunnelFlowPage.transientFunnel?.importType === 'import'
  ) {
    return state.multiFunnelFlowPage.transientFunnel;
  }
};

export const selectTransientCreateSfFunnel = (state: RootState) => {
  if (
    state.multiFunnelFlowPage.transientFunnel?.type === FunnelType.SALESFORCE &&
    state.multiFunnelFlowPage.transientFunnel?.importType === 'create'
  ) {
    return state.multiFunnelFlowPage.transientFunnel;
  }
};

export const selectTransientSfFunnel = (state: RootState) => {
  if (state.multiFunnelFlowPage.transientFunnel?.type === FunnelType.SALESFORCE) {
    return state.multiFunnelFlowPage.transientFunnel;
  }
};

export const selectTransientCreateThirdPartyFunnel = (state: RootState) => {
  if (
    state.multiFunnelFlowPage.transientFunnel?.type === FunnelType.THIRD_PARTY &&
    state.multiFunnelFlowPage.transientFunnel?.importType === 'create'
  ) {
    return state.multiFunnelFlowPage.transientFunnel;
  }
};

export const selectTransientImportThirdPartyFunnel = (state: RootState) => {
  if (
    state.multiFunnelFlowPage.transientFunnel?.type === FunnelType.THIRD_PARTY &&
    state.multiFunnelFlowPage.transientFunnel?.importType === 'import'
  ) {
    return state.multiFunnelFlowPage.transientFunnel;
  }
};

export const selectHasUnsavedChanges = (state: RootState) =>
  state.multiFunnelFlowPage.hasUnsavedChanges;

export const selectIsCreateOrAddFunnelDialogOpen = (state: RootState) =>
  state.multiFunnelFlowPage.isCreateOrAddDialogOpen;

export const selectIsPageLoading = (state: RootState) => state.multiFunnelFlowPage.isPageLoading;
export const selectHasFetchingErrors = (state: RootState) =>
  state.multiFunnelFlowPage.hasFetchingErrors;

export const selectDeployment = (state: RootState) => state.multiFunnelFlowPage.deployment;

export const selectIsFreshlyCreatedFunnelMap = (state: RootState) =>
  Boolean(state.multiFunnelFlowPage.isFreshlyCreatedFunnelMap);

export const selectIsLoadingCursor = (state: RootState) =>
  Boolean(state.multiFunnelFlowPage.isLoadingCursor);

export const {
  setFunnelStageMetadata,
  updateStageMetadataForLeadingField,
  removeAllTransientFunnelData,
  closeIsUsedInFunnelMapsDialog,
  setIsUsedInFunnelMapsDialogOpen,
  setMapsForIsUsedInFunnelMaps,
  setTransientFunnel,
  removeNewFunnelTransientData,
  dropTransientNewFunnelInPlace,
  setSfNewFunnelTransientDataStage,
  setHasUnsavedChanges,
  setIsCreateOrAddDialogOpen,
  setIsPageLoading,
  setHasFetchingErrors,
  setDeployment,
  setFunnelMapIsFreshlyCreated,
  addPlacingTemplate,
  setIsLoadingCursor,
} = multiFunnelFlowPageSlice.actions;

export default multiFunnelFlowPageSlice.reducer;
